// Utility functions for date handling

/**
 * Converts a German date string (dd.mm.yyyy) to ISO format (yyyy-MM-dd)
 */
export const germanDateToIsoDate = (germanDate: string): string => {
  if (!germanDate) return ''
  const parts = germanDate.split('.')
  if (parts.length !== 3) return ''
  return `${parts[2]}-${parts[1]}-${parts[0]}`
}

/**
 * Converts an ISO date string (yyyy-MM-dd) to German format (dd.mm.yyyy)
 */
export const isoDateToGermanDate = (isoDate: string): string => {
  if (!isoDate) return ''
  const parts = isoDate.split('-')
  if (parts.length !== 3) return ''
  return `${parts[2]}.${parts[1]}.${parts[0]}`
}

/**
 * Returns today's calendar day as an ISO `yyyy-MM-dd` string in UTC.
 *
 * Use this when comparing against backend `DateField` values stored without
 * timezone semantics — the Django app gates on `date.today()`, which on
 * containers without an explicit OS `TZ` resolves to UTC. Comparing in UTC
 * keeps the frontend in lockstep with backend gating across the day boundary.
 */
export const getTodayIsoUtc = (): string => {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${now.getUTCFullYear()}-${pad(now.getUTCMonth() + 1)}-${pad(now.getUTCDate())}`
}

/**
 * Normalizes any date string to ISO format (yyyy-MM-dd)
 */
export const normalizeToIsoDate = (dateString: string): string => {
  if (!dateString) return ''
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return dateString
  } else if (dateString.match(/^\d{2}\.\d{2}\.\d{4}$/)) {
    return germanDateToIsoDate(dateString)
  } else {
    try {
      const dateObj = new Date(dateString)
      if (!isNaN(dateObj.getTime())) {
        const year = dateObj.getFullYear()
        const month = String(dateObj.getMonth() + 1).padStart(2, '0')
        const day = String(dateObj.getDate()).padStart(2, '0')
        return `${year}-${month}-${day}`
      }
    } catch (e) {
      console.error('Invalid date format:', dateString)
    }
  }
  return ''
}
